import { createApp } from "vue";
import { createPinia } from "pinia";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App.vue";
import "shepherd.js/dist/css/shepherd.css";
import "./style.css";

// Analytics
import { initAnalytics } from "@onenterframe/analytics";

// Import views
import Products from "./views/Products.vue";
import Dashboard from "./views/Dashboard.vue";
import Login from "./views/Login.vue";
import Join from "./views/Join.vue";
import BuyCredits from "./views/BuyCredits.vue";
import Subscription from "./views/Subscription.vue";
import Usage from "./views/Usage.vue";
import Courses from "./views/Courses.vue";
import Organization from "./views/Organization.vue";
import AuthCallback from "./views/AuthCallback.vue";
import ForgotPassword from "./views/ForgotPassword.vue";
import ResetPassword from "./views/ResetPassword.vue";

// Admin views
import AdminDashboard from "./views/admin/AdminDashboard.vue";
import AdminServices from "./views/admin/AdminServices.vue";
// import AdminCustomers from "./views/admin/AdminCustomers.vue";
// import AdminSubscriptions from "./views/admin/AdminSubscriptions.vue";
// import AdminAnalytics from "./views/admin/AdminAnalytics.vue";
// import AdminLogs from "./views/admin/AdminLogs.vue";

// Router configuration
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "Products",
      component: Products,
      meta: { requiresAuth: false }, // Let App.vue handle showing landing page
    },
    {
      path: "/dashboard",
      name: "Dashboard",
      component: Dashboard,
      meta: { requiresAuth: true },
    },
    {
      path: "/login",
      name: "Login",
      component: Login,
    },
    {
      path: "/signup",
      name: "Signup",
      redirect: (to) => {
        // Redirect /signup to /login?mode=signup, preserving any redirect param
        return {
          name: "Login",
          query: {
            mode: "signup",
            ...(to.query.redirect ? { redirect: to.query.redirect } : {}),
          },
        };
      },
    },
    {
      path: "/join",
      name: "Join",
      component: Join,
    },
    {
      path: "/auth/callback",
      name: "AuthCallback",
      component: AuthCallback,
    },
    {
      path: "/forgot-password",
      name: "ForgotPassword",
      component: ForgotPassword,
    },
    {
      path: "/reset-password",
      name: "ResetPassword",
      component: ResetPassword,
    },
    {
      path: "/pricing",
      name: "SparkCalculator",
      component: () => import("./views/SparkCalculator.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/buy",
      name: "BuyCredits",
      component: BuyCredits,
      meta: { requiresAuth: true },
    },
    {
      path: "/subscription",
      name: "Subscription",
      component: Subscription,
      meta: { requiresAuth: true },
    },
    {
      path: "/usage",
      name: "Usage",
      component: Usage,
      meta: { requiresAuth: true },
    },
    {
      path: "/courses",
      name: "Courses",
      component: Courses,
      meta: { requiresAuth: true },
    },
    {
      path: "/organization",
      name: "Organization",
      component: Organization,
      meta: { requiresAuth: true },
    },
    {
      path: "/admin",
      name: "AdminDashboard",
      component: AdminDashboard,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: "/admin/customers",
      name: "AdminCustomers",
      component: () => import("./views/admin/AdminCustomers.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/products",
      name: "AdminServices",
      component: AdminServices,
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/subscriptions",
      name: "AdminSubscriptions",
      component: () => import("./views/admin/AdminSubscriptions.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/analytics",
      name: "AdminAnalytics",
      component: () => import("./views/admin/AdminAnalytics.vue"),
      meta: { requiresAuth: true },
    },
    {
      path: "/admin/logs",
      name: "AdminLogs",
      component: () => import("./views/admin/AdminLogs.vue"),
      meta: { requiresAuth: true },
    },
  ],
});

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(router);

// Initialize Customer.io analytics with router for auto page tracking
initAnalytics({ router, appName: 'sparks' });

// Auth guard
router.beforeEach(async (to, from, next) => {
  console.log("[AUTH] Route guard:", {
    to: to.path, 
    from: from.path, 
    name: to.name 
  });
  
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);
  
  console.log("[AUTH] Route requirements:", { requiresAuth, requiresAdmin });

  if (requiresAuth) {
    // Check if user is authenticated (check cookie)
    const cookies = document.cookie.split(";");
    console.log("[AUTH] All cookies:", cookies);
    const authCookie = cookies.find((cookie) =>
      cookie.trim().startsWith("ha_token=")
    );
    console.log("[AUTH] Auth cookie found:", !!authCookie);

    if (!authCookie) {
      // Only redirect to login if we're not already there
      if (to.name !== "Login") {
        next({ name: "Login", query: { redirect: to.fullPath } });
      } else {
        next();
      }
      return;
    }

    // Try to parse the cookie to ensure it's valid
    try {
      const cookieValue = authCookie.split("=")[1];
      // Decode URL-encoded cookie value
      const decodedCookieValue = decodeURIComponent(cookieValue);
      const decoded = JSON.parse(atob(decodedCookieValue));
      // Support both new format (access_token) and legacy format (token)
      const accessToken = decoded.access_token || decoded.token;
      if (!accessToken || !decoded.user_id) {
        console.log("[WARN] Cookie missing required fields:", { hasToken: !!accessToken, hasUserId: !!decoded.user_id });
        if (to.name !== "Login") {
          next({ name: "Login", query: { redirect: to.fullPath } });
        } else {
          next();
        }
        return;
      }
    } catch (error) {
      // Invalid cookie format
      console.error("[ERROR] Invalid auth cookie format:", error);
      if (to.name !== "Login") {
        next({ name: "Login", query: { redirect: to.fullPath } });
      } else {
        next();
      }
      return;
    }

    // Check admin access if required
    if (requiresAdmin) {
      // We need to get the auth store to check admin status
      // This will be available after the app is initialized
      const { useAuthStore } = await import('./stores/auth');
      const authStore = useAuthStore();
      
      // Ensure auth is initialized
      if (!authStore.isInitialized) {
        await authStore.initialize();
      }

      console.log("[AUTH] Admin check:", {
        user: authStore.user,
        role: authStore.user?.role,
        isAdmin: authStore.isAdmin
      });

      if (!authStore.isAdmin) {
        console.log("[AUTH] User is not admin, redirecting to dashboard");
        next({ name: "Dashboard" }); // Redirect non-admins to regular dashboard
        return;
      }
    }
  }

  next();
});

app.mount("#app");
